import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Stack from 'components/core/Stack';
import { useSelector } from 'react-redux';
import { lookupTableClasses } from 'utils/LookupTableUtils';
import Autocomplete from 'components/core/Autocomplete';
import ValuesTable from 'components/ValuesTable';
import Input from 'components/core/Input';
import Button from 'components/core/Button';
import AddIcon from '@mui/icons-material/Add';
import CreateModal from 'components/CreateModal';

import lookupTableActions from '../actions/lookupTableActions';

const SystemAdmin = () => {
    const me = useSelector(({ profile }) => profile.me);
    const {tableName} = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { list: listValues, hasMore, isLoading } = useSelector(({ lookupTable }) => lookupTable);
    const [sorting, setSorting] = useState({ field: 'label', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const pageSize = 50;

    const tableOptions = [
        ...lookupTableClasses.map(ltc => ({ ...ltc, canEdit: true })),
        {
            className: 'Event',
            classNameLabel: 'Event',
            fields: ['name', 'description', 'note', 'type', 'startDate', 'endDate'],
            searchFields: ['name', 'description'],
            hasView: true,
            canEdit: true
        },
        {
            className: 'Location',
            classNameLabel: 'Location',
            fields: ['label', 'parent.label', 'town.label', 'coordinates.latitude', 'coordinates.longitude'],
            searchFields: ['label'],
            hasView: true,
            canEdit: true,
            fieldLabels: {
                'label': 'Location Name',
                'parent.label': 'Parent Location',
                'town.label': 'Town',
                'coordinates.latitude': 'Latitude',
                'coordinates.longitude': 'Longitude'
            }
        },
        {
            className: 'Person',
            classNameLabel: 'Person',
            fields: ['firstName', 'lastName', 'deceased', 'subjects', 'alternativeNames'],
            searchFields: ['firstName', 'lastName'],
            canEdit: true
        },
        {
            className: 'Taxonomy',
            classNameLabel: 'Taxonomy',
            fields: ['primaryName'],
            searchFields: ['primaryName'],
            hasView: true,
            canEdit: true
        },
        {
            className: 'Observation',
            classNameLabel: 'Observation',
            fields: ['label', 'name.name', 'date'],
            searchFields: ['name.name', 'label'],
            hasView: true,
            canEdit: true,
            fieldLabels: {
                'label': 'Observation Label',
                'name.name': 'Species Name',
                'date': 'Date'
            }
        },
        {
            className: 'Evidence',
            classNameLabel: 'Evidence',
            fields: ['label', 'evidenceFormat.label', 'sourceOwned.label'],
            searchFields: ['label'],
            hasView: true,
            canEdit: true,
            fieldLabels: {
                'label': 'Evidence Name',
                'evidenceFormat.label': 'Format',
                'sourceOwned.label': 'Source Owned',
            }
        },
        {
            className: 'User',
            classNameLabel: 'User',
            fields: ['username'],
            searchFields: ['username'],
            canEdit: false
        }
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (me && me.get('type') !== 'ADMIN') {
            navigate('/');
        }
    }, [me, navigate]);

    useEffect(() => {
        if(tableName) {
            setCurrentPage(0);
            setSorting({ field: 'label', direction: 'asc' });
            setSearchTerm('');
            setDebouncedSearchTerm('');
        }
    }, [tableName]);

    useEffect(() => {
        if (tableName) {
            const selectedTable = tableOptions.find(table => table.className === tableName);
            
            setCurrentPage(0);
            dispatch(lookupTableActions.fetchTableList(tableName, {
                skip: 0,
                limit: pageSize,
                sortField: sorting.field,
                sortDirection: sorting.direction === 'asc' ? 'ascending' : 'descending',
                searchTerm: debouncedSearchTerm,
                searchFields: selectedTable?.searchFields || []
            }));
        }
    }, [dispatch, tableName, sorting, debouncedSearchTerm]);

    const handleTableSelection = (selectedOption) => {
        if (selectedOption) {
            navigate(`/system-admin/${selectedOption.className}`);
        }
    };

    const handleSortChange = (field) => {
        setSorting(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleLoadMore = () => {
        if (!isLoading && hasMore) {
            const selectedTable = tableOptions.find(table => table.className === tableName);
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            
            dispatch(lookupTableActions.fetchTableList(tableName, {
                skip: nextPage * pageSize,
                limit: pageSize,
                sortField: sorting.field,
                sortDirection: sorting.direction === 'asc' ? 'ascending' : 'descending',
                searchTerm: debouncedSearchTerm,
                searchFields: selectedTable?.searchFields || [],
                append: true
            }));
        }
    };

    const selectedTable = tableName ? tableOptions.find(table => table.className === tableName) : null;

    
    if (!me || me.get('type') !== 'ADMIN') {
        return null;
    }


    return (
        <Stack
            sx={{
                alignItems: 'center',
                height: '100vh',
                padding: 3,
                position: 'relative',
            }}
        >
            <Stack 
                sx={{ 
                    width: '100%',
                    maxWidth: '1200px',
                    position: 'absolute',
                    top: '10vh',
                    alignItems: 'center',
                }}
            >
                <Autocomplete
                    label="Select Table"
                    options={tableOptions}
                    value={selectedTable}
                    onChange={handleTableSelection}
                    getOptionLabel={(option) => option.classNameLabel}
                    sx={{ minWidth: 300 }}
                />
            </Stack>
            {tableName && (
                <Stack 
                    direction="row" 
                    spacing={2} 
                    sx={{ 
                        width: '100%',
                        maxWidth: '1200px',
                        position: 'absolute',
                        top: '18vh',
                    }}
                >
                    <Stack sx={{ width: '75%' }}>
                        {selectedTable?.searchFields && (
                            <Input
                                placeholder={`Search`}
                                value={searchTerm}
                                onChange={setSearchTerm}
                                sx={{ width: '100%' }}
                            />
                        )}
                    </Stack>
                    <Stack sx={{ width: '25%' }}>
                        <Button
                            startDecorator={<AddIcon />}
                            onClick={() => {
                                if (selectedTable.className === 'Taxonomy') {
                                    navigate('/taxonomy/add');
                                } else if (selectedTable.className === 'Observation') {
                                    navigate('/observation/add');
                                } else if (selectedTable.className === 'Evidence') {
                                    navigate('/evidence/add');
                                } else {
                                    setCreateModalOpen(true);
                                }
                            }}
                            sx={{ width: '100%' }}
                        >
                            Add New {selectedTable.classNameLabel}
                        </Button>
                    </Stack>
                </Stack>
            )}
            {tableName && (
                <Stack 
                    sx={{ 
                        width: '100%',
                        maxWidth: '1200px',
                        position: 'absolute',
                        top: '25vh',
                        height: 'calc(75vh - 3rem)',
                        overflow: 'hidden',
                    }}
                >
                    <ValuesTable
                        tableName={tableName}
                        fields={selectedTable ? selectedTable.fields : []}
                        fieldLabels={selectedTable?.fieldLabels}
                        values={listValues}
                        sorting={sorting}
                        onSortChange={handleSortChange}
                        onLoadMore={handleLoadMore}
                        hasMore={hasMore}
                        isLoading={isLoading}
                        hasView={selectedTable ? selectedTable.hasView : false}
                        canEdit={selectedTable ? selectedTable.canEdit : false}
                        onEditComplete={() => {
                            setCurrentPage(0);
                            dispatch(lookupTableActions.fetchTableList(tableName, {
                                skip: 0,
                                limit: pageSize,
                                sortField: sorting.field,
                                sortDirection: sorting.direction === 'asc' ? 'ascending' : 'descending',
                                searchTerm: debouncedSearchTerm,
                                searchFields: selectedTable?.searchFields || []
                            }));
                        }}
                    />
                </Stack>
            )}
            
            <CreateModal
                open={createModalOpen}
                onClose={() => {
                    setCreateModalOpen(false);
                    if (tableName) {
                        setCurrentPage(0);
                        dispatch(lookupTableActions.fetchTableList(tableName, {
                            skip: 0,
                            limit: pageSize,
                            sortField: sorting.field,
                            sortDirection: sorting.direction === 'asc' ? 'ascending' : 'descending',
                            searchTerm: debouncedSearchTerm,
                            searchFields: selectedTable?.searchFields || []
                        }));
                    }
                }}
                className={selectedTable?.className}
            />
        </Stack>
    );
}

export default SystemAdmin;